import { useState } from "react";
import { NavigationMenu, NavigationMenuList } from "@/components/ui/navigation-menu";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CompanyMenu } from "./menu/CompanyMenu";
import { ProductsMenu } from "./menu/ProductsMenu";
import { IndustriesMenu } from "./menu/IndustriesMenu";
import { CustomersMenu } from "./menu/CustomersMenu";
import { ConsultingMenu } from "./menu/ConsultingMenu";
import { LearningMenu } from "./menu/LearningMenu";
import { SupportMenu } from "./menu/SupportMenu";

const MenuFrame = () => {
  // Collect all images used in the MenuFrame for preloading
  const imagesToPreload = [
    "/lovable-uploads/bae7f812-cfed-47cc-9507-5e1438c9617d.png",
    "/lovable-uploads/8b686d80-4a09-4418-b434-2a788158c931.png",
    "/lovable-uploads/HR_module.jpg",
    "/lovable-uploads/Sales_module.jpg",
    "/lovable-uploads/Marketing_module.jpg",
    "/lovable-uploads/Finance_module.jpg",
    "/lovable-uploads/Projects_module.jpg",
    "/lovable-uploads/Performance2_module.jpg",
    "/lovable-uploads/Legal_module.jpg",
    "https://images.unsplash.com/photo-1604004555489-723a93d6ce74?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1569959220744-ff553533f492?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1507537297725-24a1c029d3ca?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1513258496099-48168024aec0?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1560264280-88b68371db39?w=400&h=300&fit=crop&auto=format&q=80&sat=-100",
    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=300&fit=crop&auto=format&q=80&sat=-100"
  ];

  // Preload all images
  const { allImagesLoaded } = useImagePreloader({ images: imagesToPreload });

  const [activeTab, setActiveTab] = useState({
    products: "hr",
    industries: "telecom",
    customers: "asiapacific",
    consulting: "hradvisory",
    company: "aboutus",
    learning: "resources",
    support: "helpdesk"
  });
  
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  
  const handleTabChange = (category: string, value: string) => {
    setActiveTab(prev => ({
      ...prev,
      [category]: value
    }));
  };
  
  const handleContactSuccess = () => {
    setShowSuccessDialog(true);
  };
  
  return (
    <>
      <div className="w-full bg-gray-100 py-2 px-3 flex justify-between items-center relative z-50">
        {/* Left side menu with Navigation Menu */}
          <NavigationMenu className="w-full max-w-full flex-1 min-w-0" aria-label="Primary navigation">
            <div className="overflow-x-auto lg:overflow-visible touch-pan-x no-scrollbar">
              <NavigationMenuList className="flex items-center space-x-4 whitespace-nowrap flex-nowrap min-w-max shrink-0">
              <CompanyMenu
                activeTab={activeTab.company}
                onTabChange={value => handleTabChange("company", value)}
                allImagesLoaded={allImagesLoaded}
              />

              <ProductsMenu
                activeTab={activeTab.products}
                onTabChange={value => handleTabChange("products", value)}
                allImagesLoaded={allImagesLoaded}
              />

              <IndustriesMenu
                activeTab={activeTab.industries}
                onTabChange={value => handleTabChange("industries", value)}
                allImagesLoaded={allImagesLoaded}
              />

              <CustomersMenu
                activeTab={activeTab.customers}
                onTabChange={value => handleTabChange("customers", value)}
                allImagesLoaded={allImagesLoaded}
              />

              <ConsultingMenu
                activeTab={activeTab.consulting}
                onTabChange={value => handleTabChange("consulting", value)}
                allImagesLoaded={allImagesLoaded}
              />

              <LearningMenu
                activeTab={activeTab.learning}
                onTabChange={value => handleTabChange("learning", value)}
                allImagesLoaded={allImagesLoaded}
              />

              <SupportMenu
                activeTab={activeTab.support}
                onTabChange={value => handleTabChange("support", value)}
                allImagesLoaded={allImagesLoaded}
                onContactSuccess={handleContactSuccess}
              />
            </NavigationMenuList>
          </div>
        </NavigationMenu>

        {/* Right side empty for now */}
        <div className="hidden sm:flex items-center space-x-4">
          {/* Add any right-side elements here if needed */}
        </div>
      </div>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-white w-[370px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black w-[150px]">Message sent</AlertDialogTitle>
            <AlertDialogDescription className="text-black w-[330px]">
              Thank you for contacting us. We'll get back to you soon.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogAction 
            onClick={() => setShowSuccessDialog(false)}
            className="bg-primary text-white hover:bg-primary/90 align-center w-[70px]"
          >
            OK
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MenuFrame;
